import { useCallback, useEffect, useRef, useState } from "react";
import { DataMessages, DataState, useData } from "../core/hooks/useData";
import { useDebounce } from "../core/hooks/useDebounce";
import * as pdf from "../core/pdf/types";
import { Table, TableProps } from "../core/ui/Table";
import { ContainerElement, ContainerState, EventContext, PresenterMessages, PresenterState, ReportElement, useContainer, usePresenter, usePresenterEvents } from "./types"
import { useOptions } from "./Container";
import { broadcast, useChannel } from "../core/hooks/useChannel";
import { useFieldChannelID } from "./Field";
import { newID } from "../core/utils/unique";
import { Icon } from "../core/ui/icons";
import { getOffsetTopBackTo } from "../core/utils/dom";
import { isArray } from "rant-utils";
import { RowRef } from "./InlineReport";
import { NamedScopeObject } from "../core/utils/script";
import { last } from "../core/utils/array";
import { DataEntryCustomScriptFunctions } from "./runScript";

export const ReportMessages = {
	REGISTER: "report/register",
	UNREGISTER: "report/unregister",

	SET_FOCUSED_INDEX: "report/set-focused-index",
	DELETE_FOCUSED: "report/delete-focused",

	CLEAR_FOCUSED: "report/clear-focused",

	TABLE_ROW_CLICK:  "table-row-click",

	GET_TOP: "report/get-top",
};

// interface RowRef {
// 	rowIndex: number,
// 	rowDef: pdf.TableRow,
// 	scope: any,
// 	scopeName?: string,
// };


export function Report(props: {
	def: ReportElement,

	fieldChannelID?: string,
}) {
	const presenter = usePresenter();
	const container = useContainer();
	const data = useData();

	const [token, setToken] = useDebounce(50, {});

	usePresenterEvents(presenter, useCallback((msg: any) => {
		if (msg.type === DataMessages.CHANGE) {
			if (props.def.isStatic) return;
			if (props.def.id === "pricingReport") console.log("report refresh");
			setToken({});

		} else if (msg.type === PresenterMessages.RECALC) {
			if (msg.prop !==  props.def.id) return;
			if (props.def.id === "pricingReport") console.log("report refresh");
			setToken({});
		}
	}, []));

	const containerChannel = useRef("");

	const fieldChannelRef = useFieldChannelID();
	useChannel(fieldChannelRef.current, useCallback((msg: any) => {
		if (msg.type === ReportMessages.REGISTER) {
			containerChannel.current = msg.containerChannel;
		
		} else if (msg.type === ReportMessages.UNREGISTER) {
			containerChannel.current = "";

		} else if (msg.type === ReportMessages.SET_FOCUSED_INDEX) {
			// pass message on to the table
			broadcast(channelID.current, msg);
			setToken({});

		} else if (msg.type === ReportMessages.CLEAR_FOCUSED) {
			broadcast(channelID.current, msg);

		} else if (msg.type === ReportMessages.GET_TOP) {
			const el = document.getElementById(channelID.current);
			msg.top = getOffsetTopBackTo(el, msg.container);
		
		}

	}, [fieldChannelRef.current]));

	const surroundingOptions = useOptions();

	const channelID = useRef("");
	if (!channelID.current) channelID.current = newID();

	useChannel(channelID.current, useCallback((msg) => {

		if (msg.type === ReportMessages.TABLE_ROW_CLICK) {
			if (msg.rowIndex >= 0) {
				broadcast(containerChannel.current, {
					type: ReportMessages.SET_FOCUSED_INDEX,
					index: msg.rowIndex,
				});
			}
		}

	}, [channelID.current]));


	if (presenter.designMode) {
		return <div>Report to display: <div className="tag">{props.def.report}</div></div>
	}

	const eventContext: EventContext = {
		presenter,
		container,
		//data: data,
		def: props.def,
		type: "change",
		options: surroundingOptions,

		dataWindows: [
			// {
			// 	name: "data",
			// 	obj: data.value,
			// }
		],
		debugContext: [
			"Report"
		]

	};

	return (
		<HtmlComposer
			def={props.def}
			eventContext={eventContext}

			channel={channelID.current}
		/>
	)
}


export function HtmlComposer(props: {
	def: ReportElement,
	eventContext: EventContext,

	channel: string, // id of the channel to broadcast events to
}) {
	const { def, eventContext } = props;
	const { presenter } = eventContext;

	const structure = presenter.getReport(def.report);
	if (!structure) return null;

	let dataWindows = [
		{
			name: "container",
			obj: eventContext.container,
		}
	] as NamedScopeObject[];

	// function updateScope() {
	// 	eventContext.container = last(dataWindows)?.obj;

	// 	// if (scriptContext.scope && scriptContext.scope.length > 0) {
	// 	// 	const s = scriptContext.scope;
	// 	// 	const currentScope = s[scriptContext.scope!.length-1];
	// 	// 	scriptContext.container = currentScope.obj;
			
	// 	// 	//scriptContext.data = currentScope.obj.data;	
	// 	// }
	// }


	function compose() {
		const result = [];

		for(let s of structure.sections) {
			result.push(section(s));
		}

		return (
			result
		);
	}

	function section(s: pdf.Section) {
		return content(s.content);
	}

	function content(content: pdf.AllContent[]) {
		const result = [];

		for(let c of content) {
			if (c.visibility) {
				const visible = presenter.scriptEngine.run(c.visibility, eventContext, dataWindows);
				if (visible !== true) continue;
			}


			if (c.type === "table") {
				result.push(table(c as pdf.Table));
			
			} else if (c.type === "p") {
				result.push(p(c as pdf.Paragraph));
			
			} else if (c.type === "heading") {
				result.push(heading(c as pdf.Heading));

			} else if (c.type === "list") {
				result.push(list(c as pdf.List));

			} else if (c.type === "tag") {
				result.push(tag(c as pdf.Tag));

			} else if (c.type === "group") {
				result.push(group(c as pdf.Group));

			}

		}
		return result;
	}

	
	function iterateOverList(
		//listName: string, scopeName: string, 
		def: pdf.IterationProps,
		renderItem: (item: any) => void
	) {
		const ref = eventContext.container.find(def.list!);
		if (ref && ref.field.type === "container") {
			const cont = ref.field as ContainerElement;
			if (cont.repeat && cont.repeat.itemName) {
				// repeating container, the child containers should be the instances
				for (let childContainer of ref.container.childContainers) {
					if (childContainer.def.id !== def.list!) continue;

					dataWindows.push({
						name: def.scopeName!,
						obj: childContainer.data.value,
					});

					let include = true;
					if (def.filter) {
						include = presenter.scriptEngine.run(def.filter, eventContext, dataWindows);
					}	

					if (include) {
						renderItem(childContainer.data.value);
					}

					dataWindows.pop();
				}
			}
		} else {
			// fall back to try and find object
			const arr = presenter.findValue(def.list!);
			if (arr && isArray(arr)) {
				for(let item of arr) {
					dataWindows.push({
						name: def.scopeName!,
						obj: item,
					});

					let include = true;
					if (def.filter) {
						include = presenter.scriptEngine.run(def.filter, eventContext, dataWindows);
					}	

					if (include) {					
						renderItem(item);
					}

					dataWindows.pop();
				}
			}
		}
		
	}


	function group(g: pdf.Group) {
		const items = [] as any[];

		let i = 0;
		if (g.list) {
			//updateScope();

			iterateOverList(
				g,
				(data) => {
					items.push(
						content(g.content)
					);
					i++;
				}
			);

		} else {
			items.push(
				content(g.content)
			);
			i++;
		}

		return items;
	}

	function list(list: pdf.List) {
		// NOTE: list items can be groups
		const items = [];

		let i = 0;
		for(let item of list.items) {
			if (item.list) {
				//updateScope();

				iterateOverList(
					item,
					(data) => {
						items.push(
							<li key={i}>{content(item.content)}</li>
						);
						i++;
					}
				);

			} else {
				items.push(
					<li key={i}>{content(item.content)}</li>
				);
				i++;
			}
		}

		return (
			<ul key={list.id}>{items}</ul>
		);
	}

	function heading(heading: pdf.Heading) {
		return (
			<div key={heading.id} className={"h" + heading.level}>
				{p(heading)}
			</div>
		)
	}

	function getScriptEvents(def: pdf.Content) {
		let result;

		if (def.scriptEvents) {
			result = presenter.scriptEngine.run(def.scriptEvents, eventContext, dataWindows);
		}

		if (!result) result = {};
		return result;
	}

	function p(para: pdf.Paragraph) {

		let style = {
			fontSize: (para.fontSize ? para.fontSize + "px" : undefined),
			textAlign: (para.align ? para.align : undefined),
			fontWeight: (para.bold ? "bold" : undefined),
			fontStyle: (para.italic ? "italic" : undefined),
		};

		const spans = [];
		let i = 0;
		for(let seg of para.segments) {
			let text = seg.text;

			if (seg.field) {
				if (seg.field.script) {
					//updateScope();
					const f = seg.field;

					let scriptResult = presenter.scriptEngine.run(f.script, eventContext, dataWindows);

					// does this need to be formatted?
					if (f.format || f.forceCase) {
						scriptResult = DataEntryCustomScriptFunctions.formatter(scriptResult, f.format ? f.format : "", f.forceCase);
					}

					text = (scriptResult !== undefined) ? String(scriptResult) : "";
				}
			}

			spans.push(
				<span
					key={i}
					style={{
						fontWeight: (seg.bold ? "bold" : undefined),
						fontStyle: (seg.italic ? "italic" : undefined),
						whiteSpace: "pre-wrap",
					}}
					{...getScriptEvents(para)}
				>{text}&nbsp;</span>
			);
		}

		return (
			<div
				key={para.id}
				style={style as any}				
				className={para.className}
			>{spans}</div>
		)
	}

	function tag(tag: pdf.Tag) {
		const c = content(tag.content);
		return <div className="tag incomplete">{c}</div>
	}

	function table(table: pdf.Table) {
		const cols = table.cols as any[];
		let i = 0;
		for(let c of cols) {
			c.index = i++;
		}

		// NOTE: rows can be groups so have to preprocess and make sure have all the rows necessary
		let headerRow: any = undefined;
		const rows: RowRef[] = [];
		i = 0;

		for(let r of table.rows) {
			if (r.list) {

				iterateOverList(
					r,
					(data) => {

						if (r.visibility) {
							const visible = presenter.scriptEngine.run(r.visibility, eventContext, dataWindows);
							if (visible !== true) return;
						}			

						const rd = {
							rowDef: r,
							dataWindows: [
								...dataWindows,
								
								// NOTE: the following now should be there from iterateOverList:
								// {
								// 	name: r.scopeName!,
								// 	obj: data,
								// }
							],
							rowIndex: -1,
						};
						if (!headerRow) {
							headerRow = rd;
						} else {
							rd.rowIndex = i++;
							rows.push(rd);
						}
						
					}
				);
				

			} else {

				if (r.visibility) {
					const visible = presenter.scriptEngine.run(r.visibility, eventContext, dataWindows);
					if (visible !== true) continue;
				}			

				const rd = {
					rowIndex: -1,
					rowDef: r,
					dataWindows: [
						...dataWindows,
					],
				};
				if (!headerRow) {
					headerRow = rd;
				} else {
					rd.rowIndex = i++;
					rows.push(rd);
				}
			}
		}

		return (
			<TableWithEvents
				key={table.id}

				channel={props.channel}

				style={{width: table.width ? table.width : "100%"}}

				className={table.className}

				cols={cols}
				items={rows}
				interactive={props.def.isStatic !== true}
				render={{
					//headerRow: () => null,

					colTitle: (c) => {
						if (headerRow) {
							const row = (headerRow as any) as RowRef;
							const cell = row.rowDef.cells[c.index];
	
							const orig = dataWindows;
							dataWindows = row.dataWindows;

							const result = content(cell.content);
							
							dataWindows = orig;

							return result;	
						}
					},

					colValue: (r, c) => {
						const row = (r as any) as RowRef;
						const cell = row.rowDef.cells[c.index];

						const orig = dataWindows;
						dataWindows = row.dataWindows;

						// // local vars for the attribs of this current item
						// if (row.scope && row.scope.obj && row.scope.obj.data && row.scope.obj.data.value) {
						// 	additionalScope.push(row.scope.obj.data.value);
						// } else {							
						// 	additionalScope.push(row.scope);
						// }

						const result = content(cell.content);

						dataWindows = orig;

						return result;
					}
				}}
			/>
		);
	}

	const result = compose();

	return (
		<div
			className="html-composer"
			id={props.channel}
		>
			{result}
		</div>
	);

}

export function TableWithEvents<R,C>(props: TableProps<R,C> & {
	channel: string,
}) {
	const presenter = usePresenter();

	const [selectedIndex, setSelectedIndex] = useState(-1);

	// can have other things directly communicate an index change (e.g. the Outline)
	useChannel(props.channel, useCallback((msg) => {
		if (msg.type === ReportMessages.SET_FOCUSED_INDEX) {
			setSelectedIndex(msg.index);

		} else if (msg.type === ReportMessages.CLEAR_FOCUSED) {
			setSelectedIndex(-1);

		}

	}, [props.channel]));

	// when the selected index changes notify the parent Report
	useEffect(() => {
		broadcast(props.channel, {
			type: ReportMessages.TABLE_ROW_CLICK,
			rowIndex: selectedIndex,
		});
	}, [selectedIndex]);

	const theTable = (
		<Table {...props} 

			// selectedIndex={selectedIndex}

			onClick={(e, r: any) => {
				//debugger;
				setSelectedIndex(r.rowIndex);
			}}
			noItemsMesssage=""
		/>
	);

	// let selectionTable;
	// if (selectedIndex >= 0 && props.items && selectedIndex < props.items.length) {
	// 	const selectedItems = [props.items[selectedIndex]];
	// 	selectionTable = 
	// 		<div
	// 			style={{boxShadow: "0 -15px 20px rgba(0,0,0,0.3)", position: "fixed", bottom: "0px", left: "360px", right: "0", width: "calc(100vw - 360px)", zIndex: 1000, backgroundColor: "white"}}
	// 		>
	// 			<div style={{position: "relative"}}>
	// 				<button
	// 					className="secondary flex vert align-center"
	// 					style={{position:"absolute", right: 0, top: "-20px"}}
	// 					onClick={() => {
	// 						broadcast(presenter.channelID + "/all-reports", {
	// 							type: ReportMessages.CLEAR_FOCUSED,
	// 						});
	// 					}}
	// 				><Icon type="cross" /> Close</button>
	// 				<Table {...props} 
	// 					items={selectedItems}
	// 					interactive={false}
	// 					className="secondary"
	// 					selectedIndex={0}
	// 					noItemsMesssage=""
	// 				/>	
	// 			</div>
	// 		</div>
	// };

	return (
		<>
			{theTable}
			{/* {selectionTable} */}
		</>
	);
}